import React, { useMemo, useState } from 'react';
import { Button, Card, DataTable, RowActions, ACTIONS_COL_WIDTH } from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import type { MaterialCertificate, StorageBin, StorageLocation } from '../types';
import { binLevel, type LevelTone } from '../utils/adjustInventory';
import {
  binCountLabel,
  binMaterialLabel,
  formatLastCount,
  locationKindLabel,
} from '../utils/siteDrillThrough';
import BinCertificates, { CERTIFICATES_NO_PERMISSION_REASON, type CertificatesLoad } from './BinCertificates';
import BinStatusBadge from './BinStatusBadge';
import OwnerPill from './OwnerPill';
import { storageBinStatus } from '../utils/binStatus';
import { STRUCTURE_NO_PERMISSION_REASON } from '../utils/structureForms';

/**
 * Why Adjust is switched off for someone without permission. The reason is in the menu item's
 * own label (a menu item has no hover target of its own, so a `title` would never be seen);
 * see design-system.md §5.
 */
export const ADJUST_NO_PERMISSION_LABEL = 'Adjust — you do not have permission to adjust inventory';

export const ADJUST_LABEL = 'Adjust inventory';

/** Shaping the location: its name and kind, and the bins in it (materials:locations_manage). */
export interface LocationStructureActions {
  /** Each is omitted when the caller cannot manage structure; the control then shows why. */
  onEditLocation?: () => void;
  onAddBin?: () => void;
  onEditBin?: (bin: StorageBin) => void;
}

interface LocationBinsCardProps {
  location: StorageLocation;
  /** Open the Adjust inventory form for this bin. Omit to show the action switched off. */
  onAdjust?: (bin: StorageBin) => void;
  /** Open this bin's ledger history. Omit to leave the action out. */
  onHistory?: (bin: StorageBin) => void;
  /** Edit the location, add a bin, edit a bin. Omit to leave the controls out entirely. */
  structure?: LocationStructureActions;
  /** Quality certificates on this location's bins (MT-C04), keyed by bin. Omit to leave them out. */
  certificates?: {
    byBin: Map<string, MaterialCertificate[]>;
    load: CertificatesLoad;
    /** Attach to this bin. Omit to show the action switched off with the reason. */
    onAttach?: (bin: StorageBin) => void;
    onRemove?: (certificate: MaterialCertificate) => void;
  };
}

const BAR_TONE: Record<LevelTone, string> = {
  ok: 'tw-bg-success',
  low: 'tw-bg-danger',
  high: 'tw-bg-warning',
  plain: 'tw-bg-primary',
};

const TONE_NOTE: Partial<Record<LevelTone, string>> = {
  low: 'Below minimum',
  high: 'Above maximum',
};

/**
 * On-hand figure on the left and the min / max band on the right, with a bar underneath
 * showing where the figure sits and a tick at the minimum. Red when below the minimum;
 * the same fact is also in text for anyone not seeing the colour.
 */
const LevelCell: React.FC<{ bin: StorageBin }> = ({ bin }) => {
  const level = binLevel(bin);
  if (!bin.material) {
    return <span className="tw-text-content-muted">{level.label}</span>;
  }
  const note = TONE_NOTE[level.tone];
  return (
    <div className="tw-min-w-[12rem]">
      <div className="tw-mb-1 tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-text-xs">
        <span
          className={`tw-font-medium tw-tabular-nums ${level.tone === 'low' ? 'tw-text-danger' : 'tw-text-content'}`}
        >
          {level.label}
          {note ? <span className="tw-sr-only"> — {note}</span> : null}
        </span>
        {level.band ? <span className="tw-text-content-muted">{level.band}</span> : null}
      </div>
      {level.percent !== null ? (
        <div
          role="progressbar"
          aria-label={`On hand: ${level.label}${level.band ? `, ${level.band}` : ''}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(level.percent)}
          className="tw-relative tw-h-1.5 tw-w-full tw-rounded-full tw-bg-surface-hover"
        >
          <div
            className={`tw-h-full tw-rounded-full ${BAR_TONE[level.tone]}`}
            style={{ width: `${level.percent}%` }}
          />
          {level.minPercent !== null ? (
            <span
              data-slot="min-marker"
              aria-hidden="true"
              className="tw-absolute -tw-top-0.5 tw-h-2.5 tw-w-px tw-bg-content-secondary"
              style={{ left: `${level.minPercent}%` }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

function buildColumns(
  onAdjust: LocationBinsCardProps['onAdjust'],
  onHistory: LocationBinsCardProps['onHistory'],
  structure: LocationBinsCardProps['structure'],
  certificates: LocationBinsCardProps['certificates'],
  expanded: ReadonlySet<string>,
  toggle: (binId: string) => void
): DataTableColumn<StorageBin>[] {
  return [
    {
      key: 'bin',
      header: 'Bin',
      mobile: 'title',
      className: 'tw-whitespace-nowrap',
      cell: (bin) => (
        <div>
          <p className="tw-mb-0 tw-font-medium tw-text-content">{bin.code}</p>
          <OwnerPill owner={bin.owner} className="tw-mt-0.5" />
        </div>
      ),
    },
    {
      key: 'material',
      header: 'Material',
      className: 'tw-min-w-[13rem]',
      cell: (bin) => {
        const label = binMaterialLabel(bin);
        return label ? (
          <span className="tw-line-clamp-2" title={label}>
            {label}
          </span>
        ) : (
          <span className="tw-text-content-muted">No material assigned</span>
        );
      },
    },
    {
      key: 'level',
      header: 'On hand vs. min / max',
      mobileLabel: 'On hand vs. min / max',
      className: 'tw-min-w-[12rem]',
      cell: (bin) => <LevelCell bin={bin} />,
    },
    {
      key: 'status',
      header: 'Status',
      mobileLabel: 'Status',
      className: 'tw-whitespace-nowrap',
      cell: (bin) => <BinStatusBadge status={storageBinStatus(bin)} />,
    },
    ...(certificates
      ? [
          {
            key: 'certificates',
            header: 'Certificates',
            mobileLabel: 'Certificates',
            className: 'tw-whitespace-nowrap',
            cell: (bin: StorageBin) => {
              const count = certificates.load === 'ready' ? certificates.byBin.get(bin.id)?.length ?? 0 : null;
              const open = expanded.has(bin.id);
              return (
                <Button
                  size="sm"
                  variant="link"
                  className="tw-px-0"
                  aria-expanded={open}
                  aria-controls={`bin-certificates-${bin.id}`}
                  onClick={(event: React.MouseEvent) => {
                    event.stopPropagation();
                    toggle(bin.id);
                  }}
                >
                  <i className={`bi bi-chevron-${open ? 'up' : 'down'}`} aria-hidden="true" />
                  {count === null ? 'Certificates' : count === 1 ? '1 certificate' : `${count} certificates`}
                </Button>
              );
            },
          } satisfies DataTableColumn<StorageBin>,
        ]
      : []),
    {
      key: 'actions',
      header: '',
      mobile: 'trailing',
      align: 'right',
      width: ACTIONS_COL_WIDTH,
      cell: (bin) => (
        <RowActions
          actions={[
            {
              kind: 'custom',
              icon: 'bi-sliders',
              label: onAdjust ? `${ADJUST_LABEL}: ${bin.code}` : ADJUST_NO_PERMISSION_LABEL,
              onClick: () => onAdjust?.(bin),
              disabled: !onAdjust,
            },
            ...(onHistory
              ? [{ kind: 'custom' as const, icon: 'bi-clock-history', label: `History: ${bin.code}`, onClick: () => onHistory(bin) }]
              : []),
            ...(structure
              ? [
                  {
                    kind: 'custom' as const,
                    icon: 'bi-pencil',
                    label: structure.onEditBin ? `Edit bin: ${bin.code}` : `Edit bin — ${STRUCTURE_NO_PERMISSION_REASON.toLowerCase()}`,
                    onClick: () => structure.onEditBin?.(bin),
                    disabled: !structure.onEditBin,
                  },
                ]
              : []),
            ...(certificates
              ? [
                  {
                    kind: 'custom' as const,
                    icon: 'bi-paperclip',
                    label: !bin.material
                      ? 'Attach certificate — this bin holds no material yet'
                      : certificates.onAttach
                        ? `Attach certificate: ${bin.code}`
                        : `Attach certificate — ${CERTIFICATES_NO_PERMISSION_REASON.toLowerCase()}`,
                    onClick: () => certificates.onAttach?.(bin),
                    disabled: !certificates.onAttach || !bin.material,
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];
}

/**
 * One storage location and its bins. Rows are keyed by bin id: a bin code is only unique
 * within its location, and the material belongs to the bin, never to the location.
 */
const LocationBinsCard: React.FC<LocationBinsCardProps> = ({ location, onAdjust, onHistory, structure, certificates }) => {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const toggle = (binId: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(binId)) next.delete(binId);
      else next.add(binId);
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => buildColumns(onAdjust, onHistory, structure, certificates, expanded, toggle), [onAdjust, onHistory, structure, certificates, expanded]);
  const lastCount = formatLastCount(location.lastCountAt);

  return (
    <Card data-testid={`location-${location.id}`} aria-labelledby={`location-title-${location.id}`} role="region">
      <Card.Header className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <div className="tw-min-w-0">
          <h2
            id={`location-title-${location.id}`}
            className="tw-mb-0 tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-content"
          >
            <span
              data-slot="location-kind"
              className="tw-shrink-0 tw-rounded-full tw-bg-surface-hover tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-content-secondary"
            >
              {locationKindLabel(location.kind)}
            </span>
            <span className="tw-min-w-0 tw-break-words">{location.name}</span>
          </h2>
          {lastCount ? (
            <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-content-muted">{lastCount}</p>
          ) : null}
        </div>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span className="tw-text-xs tw-text-content-muted">{binCountLabel(location.bins.length)}</span>
          {structure ? (
            <>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={structure.onEditLocation}
                disabled={!structure.onEditLocation}
                disabledReason={structure.onEditLocation ? undefined : STRUCTURE_NO_PERMISSION_REASON}
                aria-label={`Edit ${location.name}`}
              >
                <i className="bi bi-pencil" aria-hidden="true" />
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={structure.onAddBin}
                disabled={!structure.onAddBin}
                disabledReason={structure.onAddBin ? undefined : STRUCTURE_NO_PERMISSION_REASON}
                aria-label={`Add a bin to ${location.name}`}
              >
                <i className="bi bi-plus-lg" aria-hidden="true" />
                Add bin
              </Button>
            </>
          ) : null}
        </div>
      </Card.Header>
      <Card.Body className="tw-p-0">
        {/* min-width on the table is what makes it scroll inside its container instead of
            squashing the columns once the card is narrower than the content needs. */}
        <DataTable
          className="[&_table]:tw-min-w-[52rem]"
          columns={columns}
          data={location.bins}
          rowKey={(bin) => bin.id}
          hover
          emptyMessage="No bins recorded here yet."
          isExpanded={(bin) => expanded.has(bin.id)}
          renderExpanded={
            certificates
              ? (bin) => (
                  <div id={`bin-certificates-${bin.id}`}>
                    <BinCertificates
                      binLabel={`${location.name} · ${bin.code}`}
                      currentMaterialId={bin.material?.id ?? null}
                      certificates={certificates.byBin.get(bin.id) ?? []}
                      load={certificates.load}
                      onAttach={certificates.onAttach && bin.material ? () => certificates.onAttach?.(bin) : undefined}
                      onRemove={certificates.onRemove}
                    />
                  </div>
                )
              : undefined
          }
        />
      </Card.Body>
    </Card>
  );
};

export default LocationBinsCard;
